/**
 * fencesDeMutation — LE geste mutant du canvas, et lui seul.
 *
 * > Toute écriture agent passe par `muterSousFences`. Il n'existe pas de second
 * > chemin, et c'est le sujet du module : les trois gardes qu'il porte
 * > (époque ré-assertée, révision attendue, fenêtre de mutation par appel) sont
 * > celles de #939, et une copie qui en oublierait une seule serait exactement
 * > celle qu'on oublierait de corriger le jour où l'une d'elles change.
 *
 * Extrait de `textActionExecutors` / `styleActionExecutors`, où il vivait en
 * DEUX exemplaires identiques, au moment où le pont legacy est devenu le
 * troisième appelant (#979).
 *
 * ## Ce que chaque appel garantit
 *
 * 1. **L'époque est ré-assertée JUSTE AVANT l'appel mutant** — pas au début de
 *    l'action. Entre les deux il y a des lectures, donc des points de suspension,
 *    donc la possibilité que l'avocat ait ouvert un autre document.
 * 2. **La révision attendue est portée par l'opération** : c'est le verrou
 *    optimiste du provider, et le seul qui survive à l'asynchronisme. Une frappe
 *    de l'avocat entre la lecture et la mutation fait échouer l'appel au lieu de
 *    poser la marque au mauvais endroit.
 * 3. **`ctx.mutate` ne tient qu'UN appel** — il marque la mutation comme tentée
 *    et porte le réessai du refus passager. Il ne touche ni au mode de l'éditeur
 *    (celui du régime de suivi, #1838) ni à l'identité de l'auteur.
 */

import type { PeutEtreUnePromesse } from './executionAsynchrone';
import type { CanvasActionContext, CanvasMutationOptions } from './textActionExecutors';

declare const marqueurDeRevisionDeDocument: unique symbol;

/**
 * La révision du document **au sens du verrou optimiste**, et elle seule.
 *
 * ⚠️ **Deux surfaces publiques rendent une « revision » typée `string`, et elles
 * ne vivent PAS dans le même espace de valeurs** (mesuré le 2026-08-15 sur
 * `superdoc@2.6.0`, #980) :
 *
 * | surface | exemple | acceptée en `expectedRevision` ? |
 * |---|---|---|
 * | `doc.info().revision` | `"0"`, `"1"`, `"2"`… | ✅ — c'est CELLE-CI |
 * | `doc.blocks.list().revision` | `"sd-msuhjjxj-0-1-8lhan:pkg:1"` | ❌ `REVISION_MISMATCH` |
 *
 * ⚠️ En 2.15.1 la seconde rend la même valeur que la première (mesuré le
 * 2026-10-09). Elle ne sert que de repli quand `doc.info()` lève, et seulement
 * par `lireLInfoDuDocument`, plus bas : la règle « une fabrique, dans ce
 * fichier » tient toujours.
 *
 * Le contrat du provider le dit pour la première (« Monotonic decimal-string
 * revision counter ») et ne dit rien pour la seconde — qui est un identifiant de
 * paquet de lecture, opaque et sans ordre. Les deux étant `string`, **le
 * compilateur ne pouvait pas distinguer l'une de l'autre** : le pont a passé la
 * seconde pendant toute la bascule, et TOUTE mutation agent était refusée, y
 * compris le formatage de texte et le chercher-remplacer en production.
 *
 * D'où ce type marqué : un appelant qui reprendrait la révision d'une lecture de
 * blocs **ne compilera pas**. C'est la seule garde qui survive à l'oubli, le
 * message d'erreur du provider n'arrivant qu'à l'exécution.
 *
 * ⚠️ **Toutes les fabriques vivent dans CE fichier, et il n'en existe pas
 * d'autre** (revue #980) : `revisionCourante`, `marquerRevisionDInfo`,
 * `marquerRevisionApresPlan`, `marquerRevisionDeLAgent` et la sentinelle
 * `REVISION_ABSENTE`. Un `as
 * RevisionDeDocument` posé ailleurs rouvrirait le trou — chaque `as` isolé est
 * justifiable pris seul, et c'est précisément ainsi qu'ils se multiplient.
 * `grep -rn 'as RevisionDeDocument' apps/legalworkspace/src` ne doit rien
 * rendre hors de ce module.
 */
export type RevisionDeDocument = string & { readonly [marqueurDeRevisionDeDocument]: true };

/**
 * Marque la révision portée par un `doc.info()` — **la seule fabrique autorisée
 * hors de `revisionCourante`**.
 *
 * ⚠️ Le type marqué ne ferme le trou que si la marque est INFABRICABLE ailleurs.
 * Trois `as RevisionDeDocument` vivaient dispersés dans le code (revue #980) :
 * chacun était juste, chacun portait sa justification en commentaire, mais leur
 * existence même autorisait le suivant — rien n'aurait empêché un futur `as` sur
 * `blocks.list().revision`, qui est l'erreur exacte que ce type existe pour
 * rendre impossible.
 *
 * Passer par cette fonction rend la contrainte grep-able : toute marque est
 * désormais un appel nommé, et c'est son paramètre — un `info` — qui prouve
 * qu'on lit la bonne surface.
 */
export function marquerRevisionDInfo(info: unknown): RevisionDeDocument | undefined {
  if (typeof info !== 'object' || info === null) return undefined;
  const revision = (info as { revision?: unknown }).revision;
  return typeof revision === 'string' && revision.trim().length > 0
    ? (revision as RevisionDeDocument)
    : undefined;
}

/** Ce que `lireLInfoDuDocument` lit d'un document : `info`, et `blocks.list` pour le repli. */
interface DocumentARevision {
  info?: (input: Record<string, never>) => unknown;
  blocks?: { list?: (input: { offset: number; limit: number }) => unknown };
}

/** Documents dont `doc.info()` a déjà levé : le refus n'est journalisé qu'une fois par document. */
const infoEnEchecDejaJournalisee = new WeakSet<object>();

function messageDe(erreur: unknown): string {
  return erreur instanceof Error ? `${erreur.name}: ${erreur.message}` : String(erreur);
}

/**
 * `doc.info()` et la révision du document, avec un repli quand `info` LÈVE.
 *
 * `doc.info()` calcule bien plus que la révision (comptes, plan, styles), et ce
 * calcul lève sur certains Word : en SuperDoc 2.15.1, une zone de texte qui
 * contient une image en ligne (`w:txbxContent` > `w:drawing`, le logo d'un pied
 * de page de papier à en-tête) fait lever `WorkerTransportError: UTF-8 offset
 * must be an integer in […]` (signalement du 2026-10-09, modèle de bibliothèque
 * `c21df314`). Le document se lit et s'écrit pourtant normalement ; sans
 * révision, il ne montait pas et aucun verbe de l'agent ne pouvait le garder.
 *
 * Le repli lit `blocks.list().revision`, mesuré le 2026-10-09 sur ce modèle et
 * sur un modèle sain : même valeur que `doc.info().revision`, qui avance du même
 * pas à chaque écriture (0, 1, 2). La mesure #980 du 2026-08-15 (identifiant de
 * paquet `sd-…:pkg:1`) datait de 2.6.0 ; `horsDuCorps.ts` lit déjà la révision
 * d'une story par cette liste.
 *
 * ⚠️ Le repli ne joue que si `info` LÈVE. Un `info` qui répond sans révision
 * rend `undefined`, comme avant : chaque appelant garde son refus nommé.
 *
 * @returns `info` vaut `undefined` quand l'appel a levé et que le repli a servi.
 * @throws L'erreur de `doc.info()` quand le repli ne rend pas de révision non
 *   plus ; une erreur qui nomme les deux quand le repli lève à son tour, sans
 *   quoi celle de `doc.info()`, la première cause, serait perdue.
 */
export async function lireLInfoDuDocument(
  doc: DocumentARevision,
): Promise<{ info: unknown; revision: RevisionDeDocument | undefined }> {
  let erreurDInfo: unknown;
  try {
    const info = await doc.info?.({});
    return { info, revision: marquerRevisionDInfo(info) };
  } catch (erreur) {
    erreurDInfo = erreur;
  }
  let liste: unknown;
  try {
    liste = await doc.blocks?.list?.({ offset: 0, limit: 1 });
  } catch (erreurDeListe) {
    throw new Error(
      `editor.doc.info({}) threw ${messageDe(erreurDInfo)}; editor.doc.blocks.list() then threw ${messageDe(erreurDeListe)}`,
    );
  }
  const revision = marquerRevisionDInfo(liste);
  if (revision === undefined) throw erreurDInfo;
  if (!infoEnEchecDejaJournalisee.has(doc)) {
    infoEnEchecDejaJournalisee.add(doc);
    console.warn(
      `[canvas] editor.doc.info({}) threw (${messageDe(erreurDInfo)}); the document revision is read from `
      + `editor.doc.blocks.list() instead (revision=${revision}). Counts and outline from doc.info are unavailable for this document.`,
    );
  }
  return { info: undefined, revision };
}

/** La révision du document au sens du verrou optimiste — voir `lireLInfoDuDocument`. */
export async function lireLaRevisionDuDocument(doc: DocumentARevision): Promise<RevisionDeDocument | undefined> {
  return (await lireLInfoDuDocument(doc)).revision;
}

/**
 * Marque la révision qu'un plan atomique appliqué rend pour le document APRÈS
 * son écriture (`mutations.apply` → `revision.after`).
 *
 * Même espace que `doc.info().revision` : mesuré le 2026-10-02 sur
 * SuperDoc 2.15.1 (`mesure-format-text-police-taille-couleur`), où
 * `revision.after` d'un plan vaut, à chaque geste, le `doc.info().revision` lu
 * juste après — y compris quand un plan fait avancer la révision de plusieurs
 * crans. C'est la seule révision qui dise « l'état laissé par CETTE écriture »
 * sans fenêtre : une lecture de `doc.info()` après coup peut déjà porter une
 * frappe de l'avocat.
 *
 * @returns `undefined` si le reçu n'en porte pas : rien ne prouve alors l'état
 *   laissé par l'écriture.
 */
export function marquerRevisionApresPlan(recu: unknown): RevisionDeDocument | undefined {
  if (typeof recu !== 'object' || recu === null) return undefined;
  return marquerRevisionDInfo({ revision: (recu as { revision?: { after?: unknown } | null }).revision?.after });
}

/**
 * La valeur sentinelle publiée quand le provider n'annonce AUCUNE révision.
 *
 * Ce n'est pas une révision : c'est ce qu'un instantané met dans son champ pour
 * rester lisible. Le provider la refusera si elle atteignait jamais une fence
 * (`REVISION_MISMATCH`) — un refus, jamais une mutation au mauvais endroit.
 * Elle existe pour que le repli soit NOMMÉ plutôt que forcé par un `as` isolé.
 */
export const REVISION_ABSENTE = 'unknown' as RevisionDeDocument;

/**
 * Marque une révision qui **vient de l'agent**, portée par la trame.
 *
 * Fabrique distincte parce que la justification l'est : cette valeur n'a pas été
 * relue du document, l'agent la reprend de `canvas_inspect` — dont le producteur
 * publie `doc.info().revision`, donc le même espace que le verrou. Une valeur
 * d'un autre espace serait refusée par le provider à l'exécution
 * (`REVISION_MISMATCH`), jamais appliquée en silence : le risque est un refus,
 * pas une mutation au mauvais endroit.
 */
export function marquerRevisionDeLAgent(revision: string | undefined): RevisionDeDocument | undefined {
  return revision === undefined ? undefined : (revision as RevisionDeDocument);
}

/**
 * Révision RÉELLE du document, relue à l'instant, depuis la SEULE surface que le
 * verrou optimiste accepte.
 *
 * C'est aussi la seule valeur juste pour une opération qui suit une autre
 * opération mutante : chacune fait avancer la révision, et le provider refuse
 * tout ce qui présente une valeur périmée (#939, garde (b)).
 */
export async function revisionCourante(
  ctx: CanvasActionContext,
  repli: RevisionDeDocument | '',
): Promise<RevisionDeDocument> {
  return (await lireLaRevisionDuDocument(ctx.doc)) ?? (repli as RevisionDeDocument);
}

/**
 * UN appel mutant, sous les trois gardes.
 *
 * @param etape - Nom de l'étape, porté par l'erreur d'époque : c'est lui qui dit
 *   à la relecture QUELLE mutation a été refusée, et si une autre l'avait
 *   précédée.
 */
export function muterSousFences<T>(
  ctx: CanvasActionContext,
  etape: string,
  options: CanvasMutationOptions,
  operation: () => PeutEtreUnePromesse<T>,
): Promise<T> {
  ctx.verifierEpoque(etape);
  return ctx.mutate(operation, options);
}
